import React from "react";
import ReactSelect, { type MultiValue, type StylesConfig } from "react-select";

import type { CustomSelectOption } from "./CustomSelect";

export type MultiSelectProps = {
  label?: string;
  placeholder?: string;
  options: CustomSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  error?: string;
  maxSelections?: number;
  isDisabled?: boolean;
  onMaxSelectionsReached?: () => void;
};

const baseStyles: StylesConfig<CustomSelectOption, true> = {
  option: (provided, state) => ({
    ...provided,
    fontSize: "0.875rem",
    color: "var(--color-dark-black)",
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 10,
    paddingBottom: 10,
    cursor: "pointer",
    borderBottom: "1px solid #E5E7EB",
    backgroundColor: state.isSelected ? "#F3F4F6" : state.isFocused ? "#F9FAFB" : "#FFFFFF",
  }),
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderColor: state.isFocused ? "var(--color-brand-blue)" : "#E1E1E1",
    boxShadow: "none",
    fontSize: "0.875rem",
    "&:hover": {
      borderColor: state.isFocused ? "var(--color-brand-blue)" : "#E1E1E1",
    },
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "var(--color-dark-grey)",
    "&:hover": {
      color: "var(--color-dark-black)",
    },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: "#9CA3AF",
    padding: 0,
    cursor: "pointer",
    "&:hover": {
      color: "#111827",
    },
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  menu: (provided) => ({
    ...provided,
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingLeft: 12,
    paddingRight: 12,
    gap: 4,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "var(--color-primary)",
    borderRadius: 6,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    fontSize: "0.75rem",
    color: "var(--color-dark-black)",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "var(--color-dark-grey)",
    "&:hover": {
      backgroundColor: "transparent",
      color: "var(--color-dark-black)",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: "0.875rem",
    color: "var(--color-dark-grey)",
    textAlign: "left",
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    fontSize: "0.875rem",
    color: "rgba(107, 114, 128, 0.8)",
  }),
};

export default function MultiSelect({
  label,
  placeholder = "Select",
  options,
  value,
  onChange,
  error,
  maxSelections,
  isDisabled = false,
  onMaxSelectionsReached,
}: MultiSelectProps) {
  const generatedId = React.useId();
  const selectedOptions = options.filter((option) => value.includes(option.value));

  const handleChange = (next: MultiValue<CustomSelectOption>) => {
    const nextValues = next.map((option) => option.value);
    if (maxSelections && nextValues.length > maxSelections) {
      onMaxSelectionsReached?.();
      return;
    }
    onChange(nextValues);
  };

  return (
    <div className="w-full">
      {label ? (
        <label htmlFor={generatedId} className="mb-2 block text-sm font-medium text-dark-black">
          {label}
        </label>
      ) : null}
      <ReactSelect<CustomSelectOption, true>
        inputId={generatedId}
        isMulti
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isDisabled={isDisabled}
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        styles={{
          ...baseStyles,
          control: (provided, state) => ({
            ...baseStyles.control?.(provided, state),
            border: error ? "1px solid #ef4444" : "1px solid #E1E1E1",
            minHeight: 40,
            borderRadius: 8,
          }),
        }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}
